import { useSyncExternalStore } from 'react';
import { adminLoginUrl } from '@mygpt/app/apis/request';
import {
  getWidgetToken,
  subscribeWidgetAuth,
  WIDGET_LOGIN_CALLBACK_PATH,
  WIDGET_LOGIN_MESSAGE_TYPE,
} from '@mygpt/app/apis/widgetToken';

/** 响应式读取小部件登录 token，登录 / 退出后组件自动刷新 */
export const useWidgetToken = (): string => useSyncExternalStore(subscribeWidgetAuth, getWidgetToken);

export interface WidgetLoginMessage {
  type: typeof WIDGET_LOGIN_MESSAGE_TYPE;
  token: string;
}

const isWidgetLoginMessage = (data: unknown): data is WidgetLoginMessage =>
  !!data &&
  typeof data === 'object' &&
  (data as WidgetLoginMessage).type === WIDGET_LOGIN_MESSAGE_TYPE &&
  typeof (data as WidgetLoginMessage).token === 'string' &&
  !!(data as WidgetLoginMessage).token;

const POPUP_WIDTH = 480;
const POPUP_HEIGHT = 680;

/**
 * 在顶层弹窗里打开 Admin 登录页，登录完成后跳回 WIDGET_LOGIN_CALLBACK_PATH，
 * 由回调页把 token postMessage 回当前 iframe。
 * 必须在用户点击事件里同步调用，否则会被浏览器拦截弹窗。
 */
export const getWidgetLoginUrl = (): string => {
  if (!adminLoginUrl) return '';
  const callbackUrl = `${window.location.origin}${WIDGET_LOGIN_CALLBACK_PATH}`;
  return `${adminLoginUrl}?redirect_uri=${btoa(encodeURI(callbackUrl))}`;
};

export const openWidgetLoginPopup = (): Window | null => {
  const loginUrl = getWidgetLoginUrl();
  if (!loginUrl) {
    console.error('[widgetAuth] adminLoginUrl 未初始化，无法打开登录弹窗');
    return null;
  }

  const left = Math.round(Math.max(0, window.screenX + (window.outerWidth - POPUP_WIDTH) / 2));
  const top = Math.round(Math.max(0, window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2));
  const features = `popup=yes,width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top}`;
  return window.open(loginUrl, 'gbase_widget_login', features);
};

/**
 * 监听登录弹窗回传的 token（只接受同源消息）。返回取消监听函数。
 */
export const listenWidgetLogin = (onToken: (token: string) => void): (() => void) => {
  const handler = (event: MessageEvent) => {
    if (event.origin !== window.location.origin) return;
    if (!isWidgetLoginMessage(event.data)) return;
    onToken(event.data.token);
  };
  window.addEventListener('message', handler);
  return () => window.removeEventListener('message', handler);
};
